import React from 'react';
import { useTranslation } from 'react-i18next';
import './WhatsAppMessageSamples.css';

const MessageBubble = ({ type, children }) => (
  <div className={`message-bubble ${type}`}>
    {children}
    <div className="message-time">12:34</div>
  </div>
);

const MessageSeparator = ({ text }) => (
  <div className="message-separator">
    <span>{text}</span>
  </div>
);

const PhoneMockup = ({ children }) => (
  <div className="phone-mockup">
    <div className="phone-screen">
      <div className="phone-header">
        <div className="phone-header-avatar">ZM</div>
        <div className="phone-header-info">
          <div className="phone-header-name">Zap Manejo</div>
          <div className="phone-header-status">online</div>
        </div>
      </div>
      <div className="chat-area">
        {children}
      </div>
    </div>
    <div className="phone-home-button"></div>
  </div>
);

const SampleContainer = ({ title, description, keywords, children }) => {
  const { t } = useTranslation();
  return (
    <div className="sample-container">
      <div className="sample-title">{title}</div>
      {description && <div className="sample-description">{description}</div>}
      {keywords && keywords.length > 0 && (
        <div className="keywords-container">
          <div className="keywords-label">{t('keywords_label')}</div>
          <div className="keywords-list">
            {keywords.map((kw, i) => (
              <span key={i} className="keyword-badge">{kw}</span>
            ))}
          </div>
        </div>
      )}
      <PhoneMockup>
        {children}
      </PhoneMockup>
    </div>
  );
};

// Herd Samples (Births and Deaths)
const HerdSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('births_title')}
        description={t('samples_births_description')}
      >
        <MessageSeparator text={t('samples_sep_simple_births')} />
        <MessageBubble type="user">
{`12345 m nelore
12346 f angus
12347 m brangus`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_births_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_births_with_area')} />
        <MessageBubble type="user">
{`88888 m cruzado
88889 f nelore
Fazenda Norte`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_births_response_2')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_calf_with_dam')} />
        <MessageBubble type="user">
{`calf 12345 f nelore
bezerro 67890 m angus`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_births_response_3')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('deaths_title')}
        description={t('samples_deaths_description')}
      >
        <MessageSeparator text={t('samples_sep_death_causes')} />
        <MessageBubble type="user">
          {t('samples_deaths_user_input')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_deaths_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_birth_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_birth_format_code_regular')}</code> - {t('samples_birth_format_regular')}</li>
        <li><code>{t('samples_birth_format_code_calf_en')}</code> - {t('samples_birth_format_calf_en')}</li>
        <li><code>{t('samples_birth_format_code_calf_pt')}</code> - {t('samples_birth_format_calf_pt')}</li>
        <li><code>{t('samples_birth_format_code_calf_f')}</code> - {t('samples_birth_format_calf_f')}</li>
      </ul>
      <strong>{t('samples_sex_values_label')}</strong> {t('samples_sex_values')}<br/>
      <strong>{t('samples_breeds_label')}</strong> {t('samples_breeds_list')}<br/>
      <strong>{t('samples_death_causes_label')}</strong> {t('samples_death_causes_list')}
    </div>
  </div>
);

// Climate Samples (Rain, Temperature, Weather)
const ClimateSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('rain')}
        description={t('samples_rain_description')}
      >
        <MessageSeparator text={t('samples_sep_rainfall')} />
        <MessageBubble type="user">
{`02/04 40mm
25/12 15mm
30/01 22 mm`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_rain_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_climate_with_area')} />
        <MessageBubble type="user">
{`02/04 40mm
25/12 15mm
Fazenda Norte`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_rain_area_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('temperatures')}
        description={t('samples_temp_description')}
      >
        <MessageSeparator text={t('samples_sep_temperature')} />
        <MessageBubble type="user">
{`02/04 32c
25/12 28C
30/01 35 C`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_temp_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_climate_with_area')} />
        <MessageBubble type="user">
{`30/01 35C Fazenda Norte`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_temp_area_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_weather_title')}
        description={t('samples_weather_description')}
        keywords={[t('samples_weather_keywords')]}
      >
        <MessageSeparator text={t('samples_sep_weather')} />
        <MessageBubble type="user">
          {t('samples_weather_user_input')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weather_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_climate_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_climate_format_code_rain')}</code> - {t('samples_climate_format_rain')}</li>
        <li><code>{t('samples_climate_format_code_temp')}</code> - {t('samples_climate_format_temp')}</li>
        <li><code>{t('samples_climate_format_code_weather')}</code> - {t('samples_climate_format_weather')}</li>
      </ul>
    </div>
  </div>
);

// Weight Samples
const WeightSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_weight_summary_description')}
        keywords={['weight', 'peso']}
      >
        <MessageBubble type="user">{t('samples_weight_summary_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_summary_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('weight')}
        description={t('samples_weight_description')}
        keywords={['kg']}
      >
        <MessageSeparator text={t('samples_sep_basic_weight')} />
        <MessageBubble type="user">
{`1234, 500kg`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_weight_space')} />
        <MessageBubble type="user">
{`1234, 500 kg`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_weight_date')} />
        <MessageBubble type="user">
{`1234, 500kg 15/03`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_weight_multiple')} />
        <MessageBubble type="user">
{`1234, 500kg
5678, 450 kg
9012, 600kg 20/04`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_response_2')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_weight_lot_title')}
        description={t('samples_weight_lot_description')}
        keywords={[`${t('weight_lot_prefix')}-`]}
      >
        <MessageSeparator text={t('samples_sep_weight_lot_enroll')} />
        <MessageBubble type="user">
{`${t('weight_lot_prefix')}-0001 1234 500kg`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_lot_response_enroll')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_weight_lot_existing')} />
        <MessageBubble type="user">
{`${t('weight_lot_prefix')}-0001 1234 520kg`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_lot_response_existing')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_weight_lot_date')} />
        <MessageBubble type="user">
{`${t('weight_lot_prefix')}-0001 1234 520kg 28/03`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_lot_response_existing')}
        </MessageBubble>
      </SampleContainer>
      <SampleContainer
        title={t('samples_weight_lot_score_title')}
        description={t('samples_weight_lot_score_description')}
        keywords={[`${t('weight_lot_prefix')}-`]}
      >
        <MessageSeparator text={t('samples_weight_lot_score_title')} />
        <MessageBubble type="user">
{`${t('weight_lot_prefix')}-0005 -3`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_weight_lot_score_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_weight_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_weight_format_code_basic')}</code> - {t('samples_weight_format_basic')}</li>
        <li><code>{t('samples_weight_format_code_date')}</code> - {t('samples_weight_format_date')}</li>
        <li><code>{t('samples_weight_format_code_no_comma')}</code> - {t('samples_weight_format_no_comma')}</li>
        <li><code>{t('samples_weight_format_code_lot')}</code> - {t('samples_weight_format_lot')}</li>
        <li><code>{t('samples_weight_format_code_lot_date')}</code> - {t('samples_weight_format_lot_date')}</li>
        <li><code>{t('weight_lot_prefix')}-XXXX score</code> - {t('samples_weight_lot_score_description')}</li>
      </ul>
      <strong>{t('samples_keyword_label')}</strong> {t('samples_weight_keyword_note')}
    </div>
  </div>
);

// Reproduction Samples (Lots, Animals, Status)
const ReproductionSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_repro_summary_description')}
        keywords={['reproduction', 'reprodução', 'iatf', 'repro']}
      >
        <MessageBubble type="user">{t('samples_repro_summary_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_repro_summary_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_create_lot_title')}
        description={t('samples_create_lot_description')}
        keywords={['new lot', 'novo lote', 'novo iatf', 'iniciar lote']}
      >
        <MessageSeparator text={t('samples_sep_lot_default_bull')} />
        <MessageBubble type="user">
          novo lote
        </MessageBubble>
        <MessageBubble type="system">
{`Zap Manejo criou o lote IATF-0042 usando o protocolo 'Protocolo Padrão'.

Data de Início: 2026-02-18 (Dia 0)
Touro: Jabriel

Tarefas do Dia 0:
- Inserir implante
- Aplicar GnRH

Você pode adicionar animais a este lote pelo painel.`}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_lot_specific_bull')} />
        <MessageBubble type="user">
          {t('samples_lot_specific_bull_user_input')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_lot_specific_bull_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_lot_bull_next_line')} />
        <MessageBubble type="user">
{`novo lote
touro grande`}
        </MessageBubble>
        <MessageBubble type="system">
{`Zap Manejo criou o lote IATF-0044 usando o protocolo 'Protocolo Padrão'.

Data de Início: 2026-02-18 (Dia 0)
Touro: Touro Grande

Tarefas do Dia 0:
- Inserir implante

Você pode adicionar animais a este lote pelo painel.`}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_add_animals_title')}
        description={t('samples_add_animals_description')}
      >
        <MessageSeparator text={t('samples_sep_add_default_bull')} />
        <MessageBubble type="user">
{`${t('lot_prefix')}-0042
12345
12346
12347`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_add_animals_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_add_specific_bull')} />
        <MessageBubble type="user">
{`${t('lot_prefix')}-0042
12348 angus
12349 jabriel
12350`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_add_animals_response_2')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_lot_status_title')}
        description={t('samples_lot_status_description')}
      >
        <MessageSeparator text={t('samples_sep_status_query')} />
        <MessageBubble type="user">
          {t('samples_lot_status_user_input')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_lot_status_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_repro_format_creating_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>new lot</code> - {t('samples_repro_format_new_lot')}</li>
        <li><code>new lot jabriel</code> - {t('samples_repro_format_new_lot_bull')}</li>
        <li><code>novo lote</code> + newline + <code>touro</code> - {t('samples_repro_format_novo_lote')}</li>
      </ul>
      <strong>{t('samples_repro_format_adding_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>12345</code> - {t('samples_repro_format_animal_default')}</li>
        <li><code>12345 angus</code> - {t('samples_repro_format_animal_bull')}</li>
      </ul>
      <strong>{t('samples_repro_format_iatf_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_repro_format_iatf_examples')}</code></li>
      </ul>
    </div>
  </div>
);

// Lists Samples (named lists; LIST/LISTA code, either works)
const ListsSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_lists_summary_description')}
        keywords={['lists', 'listas']}
      >
        <MessageBubble type="user">{t('samples_lists_summary_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_lists_summary_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('lists_all')}
        description={t('samples_lists_description')}
        keywords={[t('list_prefix'), `${t('list_prefix')}-XXXX`]}
      >
        <MessageSeparator text={t('samples_sep_lists_create')} />
        <MessageBubble type="user">{t('samples_lists_create_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_lists_create_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_lists_add_default')} />
        <MessageBubble type="user">
          {`${t('list_prefix')} ${t('samples_lists_item')}`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_lists_add_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_lists_add_specific')} />
        <MessageBubble type="user">
          {`${t('list_prefix')}-0001 ${t('samples_lists_item')}`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_lists_add_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_lists_query')} />
        <MessageBubble type="user">
          {`${t('list_prefix')}-0001`}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_lists_query_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div className="samples-grid" style={{ marginTop: '24px' }}>
      <SampleContainer
        title={t('samples_lists_alias_title')}
        description={t('samples_lists_alias_description')}
        keywords={[t('nicknames')]}
      >
        {['add', 'keyword', 'query', 'status'].map(key => (
          <React.Fragment key={key}>
            <MessageSeparator text={t(`samples_sep_lists_alias_${key}`)} />
            <MessageBubble type="user">{t(`samples_lists_alias_${key}_input`)}</MessageBubble>
            <MessageBubble type="system">{t(`samples_lists_alias_${key}_response`)}</MessageBubble>
          </React.Fragment>
        ))}
      </SampleContainer>

      <SampleContainer
        title={t('samples_lists_status_title')}
        description={t('samples_lists_status_description')}
        keywords={['purchased', 'comprei', 'completed', 'concluído', 'pending', 'pendente']}
      >
        {['match', 'ambiguous', 'nomatch', 'undo'].map(key => (
          <React.Fragment key={key}>
            <MessageSeparator text={t(`samples_sep_lists_status_${key}`)} />
            <MessageBubble type="user">{t(`samples_lists_status_${key}_input`)}</MessageBubble>
            <MessageBubble type="system">{t(`samples_lists_status_${key}_response`)}</MessageBubble>
          </React.Fragment>
        ))}
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_lists_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_lists_create_user_input')}</code> — {t('samples_lists_format_create')}</li>
        <li><code>{t('list_prefix')} item</code> — {t('samples_lists_format_add')}</li>
        <li><code>{t('list_prefix')}-0001 item</code> — {t('samples_lists_format_specific')}</li>
        <li><code>{t('list_prefix')}-0001</code> — {t('samples_lists_format_query')}</li>
        <li><code>{t('samples_lists_status_match_input')}</code> — {t('samples_lists_format_status')}</li>
        <li><code>{t('samples_lists_alias_add_input')}</code> — {t('samples_lists_format_alias')}</li>
      </ul>
    </div>
  </div>
);

// Finance Samples (Gain, Loss)
const FinanceSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_finance_summary_description')}
        keywords={['finances', 'finanças']}
      >
        <MessageBubble type="user">{t('samples_finance_summary_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_summary_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_finance_gain_title')}
        description={t('samples_finance_gain_description')}
        keywords={[t('samples_finance_keywords_gain')]}
      >
        <MessageSeparator text={t('samples_sep_finance_simple_gain')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_gain_simple')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_gain_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_gain_institution')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_gain_institution')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_gain_response_4')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_gain_notes')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_gain_notes')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_gain_response_2')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_gain_date')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_gain_date')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_gain_response_3')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_finance_loss_title')}
        description={t('samples_finance_loss_description')}
        keywords={[t('samples_finance_keywords_loss')]}
      >
        <MessageSeparator text={t('samples_sep_finance_simple_loss')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_loss_simple')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_loss_response_1')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_loss_institution')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_loss_institution')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_loss_response_4')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_loss_notes')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_loss_notes')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_loss_response_2')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_loss_date')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_loss_date')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_loss_response_3')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_finance_project_title')}
        description={t('samples_finance_project_description')}
        keywords={[t('samples_finance_keywords_loss'), t('samples_finance_keywords_gain')]}
      >
        <MessageSeparator text={t('samples_sep_finance_project_expense')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_project_expense')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_project_response_expense')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_finance_project_flip')} />
        <MessageBubble type="user">
          {t('samples_finance_msg_project_flip')}
        </MessageBubble>
        <MessageBubble type="system">
          {t('samples_finance_project_response_flip')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_finance_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_finance_format_code_gain')}</code> - {t('samples_finance_format_gain')}</li>
        <li><code>{t('samples_finance_format_code_loss')}</code> - {t('samples_finance_format_loss')}</li>
        <li><code>{t('samples_finance_format_code_institution')}</code> - {t('samples_finance_format_institution')}</li>
        <li><code>{t('samples_finance_format_code_notes')}</code> - {t('samples_finance_format_notes')}</li>
        <li><code>{t('samples_finance_format_code_date')}</code> - {t('samples_finance_format_date')}</li>
      </ul>
      <strong>{t('samples_finance_categories_label')}</strong> {t('samples_finance_categories_list')}
      <br />
      <strong>{t('samples_finance_institutions_label')}</strong> {t('samples_finance_institutions_list')}
    </div>
  </div>
);

// Ranch Samples (List & Switch)
const RanchSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_ranch_list_title')}
        description={t('samples_ranch_list_description')}
        keywords={['ranch', 'ranches', 'fazenda', 'fazendas']}
      >
        <MessageBubble type="user">{t('samples_ranch_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_ranch_list_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_ranch_switch_title')}
        description={t('samples_ranch_switch_description')}
        keywords={['ranch <nickname>', 'fazenda <apelido>']}
      >
        <MessageSeparator text={t('samples_sep_ranch_switch')} />
        <MessageBubble type="user">{t('samples_ranch_switch_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_ranch_switch_response')}
        </MessageBubble>

        <MessageSeparator text={t('samples_sep_ranch_already_active')} />
        <MessageBubble type="user">{t('samples_ranch_switch_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_ranch_already_active_response')}
        </MessageBubble>
      </SampleContainer>
    </div>

    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
      <strong>{t('samples_ranch_format_title')}</strong>
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
        <li><code>{t('samples_ranch_user_input')}</code> — {t('samples_ranch_format_list')}</li>
        <li><code>{t('samples_ranch_user_input')} [nickname]</code> — {t('samples_ranch_format_switch')}</li>
      </ul>
    </div>
  </div>
);

// Overview Samples
const OverviewSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_overview_description')}
        keywords={['overview', 'status', 'visão geral', 'situação']}
      >
        <MessageBubble type="user">{t('samples_overview_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_overview_response')}
        </MessageBubble>
      </SampleContainer>
    </div>
  </div>
);

// Help Samples
const HelpSamples = ({ t }) => (
  <div className="category-section">
    <div className="samples-grid">
      <SampleContainer
        title={t('samples_help_general_description')}
        keywords={['help', 'ajuda']}
      >
        <MessageBubble type="user">{t('samples_help_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_help_general_response')}
        </MessageBubble>
      </SampleContainer>

      <SampleContainer
        title={t('samples_help_feature_description')}
        keywords={['help weight', 'ajuda peso']}
      >
        <MessageBubble type="user">{t('samples_help_weight_user_input')}</MessageBubble>
        <MessageBubble type="system">
          {t('samples_help_weight_response')}
        </MessageBubble>
      </SampleContainer>
    </div>
  </div>
);

const SectionHeader = ({ title }) => (
  <div className="samples-section-header">
    <span className="samples-section-header-bar" />
    <h3 className="samples-section-header-title">{title}</h3>
  </div>
);

export const WhatsAppMessageSamples = () => {
  const { t } = useTranslation();

  return (
    <div className="whatsapp-samples">
      <OverviewSamples t={t} />

      <SectionHeader title={t('births')} />
      <HerdSamples t={t} />

      <SectionHeader title={t('climate')} />
      <ClimateSamples t={t} />

      <SectionHeader title={t('weight')} />
      <WeightSamples t={t} />

      <SectionHeader title={t('reproduction')} />
      <ReproductionSamples t={t} />

      <SectionHeader title={t('finances')} />
      <FinanceSamples t={t} />

      <SectionHeader title={t('lists')} />
      <ListsSamples t={t} />

      <SectionHeader title={t('samples_ranch_tab')} />
      <RanchSamples t={t} />

      <SectionHeader title={t('samples_help_tab')} />
      <HelpSamples t={t} />
    </div>
  );
};

export default WhatsAppMessageSamples;
